import { FC, ReactNode, useState } from 'react'
import styled from 'styled-components'
import {
  BD, SEVERIDADE, BotaoIcone, Button, Card, CELULAR, Folha, FolhaOverlay, FolhaPega, FolhaTopo, StatusBadge,
} from '../styles'
import { BarraFixa } from './BarraFixa'

const Painel = styled(Card)`
  padding: 16px;
  display: none;
  @media (min-width: 768px) {
    display: block;
    position: sticky;
    top: 16px;
    align-self: start;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  }
  @media (max-width: 1023px) {
    /* Aqui o trilho vira uma faixa no topo da coluna única: grudado, ele
       cobriria o conteúdo durante a rolagem. */
    position: static;
    order: -1;
    margin-bottom: 16px;
  }
`

const Titulo = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${BD.grayText};
  margin-bottom: 8px;
`

const Aviso = styled.div`
  padding: 12px;
  border: 1px solid ${SEVERIDADE.atencao.borda};
  border-radius: 10px;
  background: ${SEVERIDADE.atencao.bg};
  color: ${BD.black};
  font-size: 13px;
  line-height: 19px;
`

const Motivo = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${BD.grayText};
`

const Empilhadas = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`

const Pendencia = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${BD.grayBorder};
  font-size: 12px;
  color: ${BD.grayText};
`

const Larga = styled(Button)`
  width: 100%;
`

const SoNaFolha = styled.div`
  display: none;
  @media ${CELULAR} {
    display: block;
  }
`

export interface AcaoDecisao {
  rotulo: string
  rodar: () => void
  /** Escrito ao lado do botão desligado: o que destrava, não só o que falta. */
  impedimento?: string | null
  variante?: 'primary' | 'secondary'
}

interface DecisaoBase {
  estado: { bg: string; color: string; label: string }
  desde?: ReactNode
  aviso?: ReactNode
  principal?: AcaoDecisao | null
  secundarias?: AcaoDecisao[]
  pendencia?: ReactNode
  antesDasSaidas?: ReactNode
  busy?: boolean
  /** Chamado quando a folha do celular fecha: serve para desarmar confirmações. */
  aoFechar?: () => void
}

/** O formulário só existe no trilho: quem o usa cuida do próprio celular. */
export type DecisaoProps = DecisaoBase & (
  | { somenteTrilho: true; formulario?: ReactNode }
  | { somenteTrilho?: false; formulario?: never }
)

const Botao: FC<{ acao: AcaoDecisao; busy?: boolean }> = ({ acao, busy }) => (
  <div>
    <Larga $variant={acao.variante ?? 'primary'} disabled={busy || !!acao.impedimento} onClick={acao.rodar}>
      {acao.rotulo}
    </Larga>
    {acao.impedimento && <Motivo>{acao.impedimento}</Motivo>}
  </div>
)

/**
 * OP-3619 — onde se age, separado de onde se lê.
 *
 * A mesma regra de decisão em duas ancoragens: no desktop um trilho fixo ao
 * lado do conteúdo; no celular a barra presa no rodapé, com as saídas
 * secundárias numa folha. Ordem única — estado, aviso, ação primária, outras
 * saídas, pendência —, e botão bloqueado nunca some: fica desligado dizendo o
 * que o destrava.
 */
export const Decisao: FC<DecisaoProps> = ({
  estado, desde, aviso, principal, secundarias = [], pendencia, antesDasSaidas, formulario, somenteTrilho, busy, aoFechar,
}) => {
  const [aberta, setAberta] = useState(false)
  const temSecundarias = secundarias.length > 0

  const fecharFolha = () => {
    setAberta(false)
    if (aoFechar) aoFechar()
  }

  const folha = (
    <>
      <FolhaOverlay $open onClick={fecharFolha} />
      <Folha $open role="dialog" aria-label="Outras saídas">
        <FolhaPega />
        <FolhaTopo>
          <h2>Outras saídas</h2>
          <Button $variant="secondary" onClick={fecharFolha}>Fechar</Button>
        </FolhaTopo>
        {aviso && <Aviso style={{ marginBottom: 12 }}>{aviso}</Aviso>}
        {antesDasSaidas}
        <Empilhadas>
          {secundarias.map((a) => <Botao key={a.rotulo} acao={{ ...a, variante: a.variante ?? 'secondary' }} busy={busy} />)}
        </Empilhadas>
        {pendencia && <Pendencia>{pendencia}</Pendencia>}
      </Folha>
    </>
  )

  return (
    <>
      <Painel as="aside" aria-label="Decisão">
        <Titulo>Onde está</Titulo>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StatusBadge $bg={estado.bg} $color={estado.color}>{estado.label}</StatusBadge>
          {desde && <span style={{ fontSize: 12, color: BD.grayText }}>{desde}</span>}
        </div>

        {aviso && <div style={{ marginTop: 12 }}><Aviso>{aviso}</Aviso></div>}

        {formulario && <div style={{ marginTop: 16 }}>{formulario}</div>}

        {principal && (
          <>
            <Titulo style={{ marginTop: 16 }}>O que fazer agora</Titulo>
            <Botao acao={principal} busy={busy} />
          </>
        )}

        {temSecundarias && (
          <>
            <Titulo style={{ marginTop: 16 }}>Outras saídas</Titulo>
            {antesDasSaidas}
            <Empilhadas>
              {secundarias.map((a) => <Botao key={a.rotulo} acao={{ ...a, variante: a.variante ?? 'secondary' }} busy={busy} />)}
            </Empilhadas>
          </>
        )}

        {pendencia && <Pendencia>{pendencia}</Pendencia>}
      </Painel>

      {!somenteTrilho && (principal || temSecundarias) && (
        <SoNaFolha>
          <BarraFixa>
            {/* Aviso e motivo sobem junto com o botão: no celular não há espaço
                ao lado, e texto que só existe dentro da folha não é lido por
                quem nem tem motivo para abri-la. */}
            {aviso && <Aviso>{aviso}</Aviso>}
            {principal?.impedimento && <Motivo style={{ marginTop: 0 }}>{principal.impedimento}</Motivo>}
            {!temSecundarias && pendencia && <Motivo style={{ marginTop: 0 }}>{pendencia}</Motivo>}
            <div style={{ display: 'flex', gap: 10 }}>
              {temSecundarias && (
                <BotaoIcone type="button" aria-label="Outras saídas" onClick={() => setAberta(true)}>⋮</BotaoIcone>
              )}
              {principal ? (
                <Button
                  style={{ flexGrow: 1 }}
                  disabled={busy || !!principal.impedimento}
                  onClick={principal.rodar}
                >
                  {principal.rotulo}
                </Button>
              ) : (
                <Button style={{ flexGrow: 1 }} $variant="secondary" onClick={() => setAberta(true)}>Outras saídas</Button>
              )}
            </div>
          </BarraFixa>
          {aberta && folha}
        </SoNaFolha>
      )}
    </>
  )
}

export default Decisao
